import { useEffect, useMemo, useState, } from "react";
import { Template } from "./ImageGeneration/types";
import { templates } from "./ImageGeneration/templates";
import ImagePreview from "./ImageGeneration/ImagePreview";
import { generateTemplateImage } from "./ImageGeneration/generateTemplateImage";
import { downloadImage } from "./ImageGeneration/downloadImage";
import TemplatePicker from "./ImageGeneration/TemplatePicker";
import ImageActions from "./ImageGeneration/ImageActions";
import { TextTemplate } from "./TextGeneration/TextTransformer";
import { textTemplates } from "./TextGeneration/textTemplates";
import { ContentGenerator } from "./TextGeneration/contentGenerator";
import LanguagePicker from "./TextGeneration/LanguagePicker";
import TextTemplatePicker from "./TextGeneration/TextTemplatePicker";
import TextActions from "./TextGeneration/TextActions";
import PostTextPreview from "./TextGeneration/PostTextPreview";
import Card from "../Card";
import TextUploader from "./TextUploader";
import FileUploader from "./FileUploader";
import { PostType } from "./TextGeneration/contentGenerator";
import { useAuth } from "./Authentification/AuthContext";
import { createAuthFetch } from "./Authentification/apiFetch";
import { CustomTemplate } from "./ImageGeneration/customTemplateTypes";
import { createCustomTemplateApi } from "./ImageGeneration/customTemplateApi";
import { generateCustomTemplateImage } from "./ImageGeneration/generateCustomTemplateImage";

interface PostCreationProps {
  text: string;
  setText: (value: string) => void;

  file: File | null;
  setFile: (file: File | null) => void;

  setImageURL: (imageURL: string) => void;
}

export interface SavedPost {
  id: string;

  imageDataUrl: string | null;
  text: string;

  postType: PostType | null;
  usecase: string | null;

  templateName: string | null;
  language: string;

  sourceText: string;

  createdAt: string;
  updatedAt: string;
}

export default function PostCreation({ text, setText, file, setFile, setImageURL }: PostCreationProps) {
    const [currentTemplate, setCurrentTemplate] = useState<Template>(templates[0]);
    const [customTemplates, setCustomTemplates] = useState<CustomTemplate[]>([]);
    const [selectedCustomTemplate, setSelectedCustomTemplate] = useState<CustomTemplate | null>(null);
    const [transformedURL, setTransformedURL] = useState<string | null>(null);
    const [isGenerating, setIsGenerating] = useState(false);
    const [generationStep, setGenerationStep] = useState("");
    const [postText, setPostText] = useState("");
    const [isTextGenerating, setIsTextGenerating] = useState(false);
    const [selectedTextTemplate, setSelectedTextTemplate] = useState<TextTemplate>(textTemplates[0]);
    const [selectedLanguage, setSelectedLanguage] = useState<string>("english");
    const [isSavingPost, setIsSavingPost] = useState(false);
    const [saveMessage, setSaveMessage] = useState<string | null>(null);

    const { token } = useAuth();
    const customTemplateApi = useMemo(
        () => createCustomTemplateApi(token),
        [token]
    );

    const apiFetch = useMemo(
    () => createAuthFetch(token),
    [token]
    );

    const generator = new ContentGenerator();

    useEffect(() => {
  const loadCustomTemplates = async () => {
    try {
      const loaded =
        await customTemplateApi.getAll();

      setCustomTemplates(loaded);
    } catch (error) {
      console.error(
        "Eigene Templates konnten nicht geladen werden:",
        error
      );
    }
  };

  void loadCustomTemplates();
}, [customTemplateApi]);

    const handleSavePost = async () => {
    if (!postText && !transformedURL) return;

    setIsSavingPost(true);
    setSaveMessage(null);

    try {
        const response = await apiFetch("/api/postplans", {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify({
            usecases: [],
            entries: [
            {
                id: crypto.randomUUID(),

                fileName: "Manuell erstellter Post",
                linkedFileName: null,
                baseName: `manual-post-${Date.now()}`,

                postType: selectedTextTemplate.id,
                postPotential: null,
                reasoning: "Manuell in Post Creation erstellt",

                usecase: null,

                imageDataUrl: transformedURL,
                text: postText,

                scheduledDate: new Date().toISOString(),
            },
            ],
        }),
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));

      throw new Error(
        data.error ??
          `Post konnte nicht gespeichert werden (${response.status})`
      );
    }

    setSaveMessage("Post als Entwurf gespeichert");
  } catch (err) {
    console.error(err);

    setSaveMessage(
      err instanceof Error
        ? err.message
        : "Post konnte nicht gespeichert werden"
    );
  } finally {
    setIsSavingPost(false);
  }
};
    
    const handleImageGenerate = async () => {
  setIsGenerating(true);

  try {
    setGenerationStep(
      "Bild wird zusammengesetzt..."
    );

    let dataUrl: string;

    if (selectedCustomTemplate) {
      dataUrl =
        await generateCustomTemplateImage(
          file,
          text,
          selectedCustomTemplate
        );
    } else {
      dataUrl =
        await generateTemplateImage(
            file,
            text,
            currentTemplate,
            apiFetch
        );
    }

    setTransformedURL(dataUrl);
  } catch (err) {
    console.error(
      "Bild konnte nicht generiert werden:",
      err
    );
  } finally {
    setIsGenerating(false);
  }
};

    const handleTemplateSelect = (template: Template) => {
        setCurrentTemplate(template);
        setSelectedCustomTemplate(null);
        setTransformedURL(null);
    };

    const handleCustomTemplateSelect = (
        template: CustomTemplate
        ) => {
        setSelectedCustomTemplate(template);
        setTransformedURL(null);
    };

    const handleDeleteCustomTemplate = async (
  templateId: string
) => {
  const confirmed = window.confirm(
    "Soll dieses Template wirklich gelöscht werden?"
  );

  if (!confirmed) {
    return;
  }

  try {
    await customTemplateApi.remove(templateId);

    setCustomTemplates((previous) =>
      previous.filter(
        (template) => template.id !== templateId
      )
    );

    // Falls gerade das gelöschte Template ausgewählt war
    if (selectedCustomTemplate?.id === templateId) {
      setSelectedCustomTemplate(null);
      setTransformedURL(null);
    }
  } catch (error) {
    console.error(
      "Template konnte nicht gelöscht werden:",
      error
    );

    alert("Template konnte nicht gelöscht werden.");
  }
};

    const handleImageDownload = () => {
        if (!transformedURL) return;

        downloadImage(transformedURL, "Post.png");
    };

    const handleTextTemplateSwap = (template: TextTemplate) => {
        setSelectedTextTemplate(template);
        setPostText("");
    };

    const handleTextGenerate = async () => {
        setIsTextGenerating(true);

        try {
            const result = await generator.generatePost(
            text,
            selectedTextTemplate.id,
            selectedLanguage,
            apiFetch
            );

            setPostText(result);
        } catch (err) {
            console.error(err);
        } finally {
            setIsTextGenerating(false);
        }
    };

return (
    <div className="post-creation-layout">
        <div className="post-creation-controls">

            <Card>
                <section className="creation-section">
                    <div>
                        <h2>Inhalte</h2>
                    </div>

                    <div className="section-content">
                        <TextUploader
                            text={text}
                            setText={setText}
                        />

                        <FileUploader
                            file={file}
                            setFile={setFile}
                            setImageURL={setImageURL}
                        />
                    </div>
                </section>

                <section className="creation-section">
                    <div>
                        <h2>Design</h2>
                    </div>

                    <div className="section-content">
                        <TemplatePicker
                            templates={templates}
                            currentTemplate={currentTemplate}
                            disabled={isGenerating}
                            onSelect={handleTemplateSelect}
                        />

                        {customTemplates.length > 0 && (
                            <div
                                style={{
                                    marginTop: 20,
                                }}
                            >
                                <label
                                    className="field-label"
                                    style={{
                                        display: "block",
                                        marginBottom: 8,
                                    }}
                                >
                                    Eigene Templates
                                </label>

                                <div
                                    style={{
                                        display: "flex",
                                        flexWrap: "wrap",
                                        gap: 10,
                                    }}
                                >
                                    {customTemplates.map((template) => {
                                        const selected =
                                            selectedCustomTemplate?.id ===
                                            template.id;

                                        return (
                                            <div
                                                key={template.id}
                                                style={{
                                                    position: "relative",
                                                    width: 140,
                                                }}
                                            >
                                                <button
                                                    type="button"
                                                    disabled={isGenerating}
                                                    onClick={() =>
                                                        handleCustomTemplateSelect(
                                                            template
                                                        )
                                                    }
                                                    style={{
                                                        width: "100%",
                                                        padding: 8,
                                                        border: selected
                                                            ? "2px solid #007f7b"
                                                            : "1px solid #ccc",
                                                        borderRadius: 8,
                                                        background: "#fff",
                                                        cursor: isGenerating
                                                            ? "default"
                                                            : "pointer",
                                                    }}
                                                >
                                                    <img
                                                        src={
                                                            template.backgroundImageUrl
                                                        }
                                                        alt={template.name}
                                                        style={{
                                                            width: "100%",
                                                            height: 80,
                                                            objectFit:
                                                                "contain",
                                                            marginBottom: 6,
                                                        }}
                                                    />

                                                    <div>
                                                        {template.name}
                                                    </div>
                                                </button>

                                                <button
                                                    type="button"
                                                    title="Template löschen"
                                                    disabled={isGenerating}
                                                    onClick={(event) => {
                                                        event.stopPropagation();

                                                        handleDeleteCustomTemplate(
                                                            template.id
                                                        );
                                                    }}
                                                    style={{
                                                        position: "absolute",
                                                        top: 4,
                                                        right: 4,
                                                        width: 24,
                                                        height: 24,
                                                        border: "none",
                                                        borderRadius: "50%",
                                                        background:
                                                            "rgba(255, 255, 255, 0.9)",
                                                        cursor: isGenerating
                                                            ? "default"
                                                            : "pointer",
                                                        fontSize: 18,
                                                        lineHeight: "20px",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent:
                                                            "center",
                                                        padding: 0,
                                                    }}
                                                >
                                                    ×
                                                </button>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        <ImageActions
                            isGenerating={isGenerating}
                            hasGeneratedImage={
                                transformedURL !== null
                            }
                            onGenerate={handleImageGenerate}
                            onDownload={handleImageDownload}
                        />
                    </div>
                </section>

                <section className="creation-section">
                    <h2>Sprache & Post-Typ</h2>

                    <div className="post-settings">
                        <div className="post-settings__group">
                            <label className="field-label">
                                Sprache
                            </label>

                            <LanguagePicker
                                selectedLanguage={
                                    selectedLanguage
                                }
                                onChange={
                                    setSelectedLanguage
                                }
                                disabled={
                                    isTextGenerating
                                }
                            />
                        </div>

                        <div className="post-settings__group">
                            <label className="field-label">
                                Post-Typ
                            </label>

                            <TextTemplatePicker
                                templates={
                                    textTemplates
                                }
                                selectedTemplate={
                                    selectedTextTemplate
                                }
                                onSelect={
                                    handleTextTemplateSwap
                                }
                                disabled={
                                    isTextGenerating
                                }
                            />
                        </div>
                    </div>

                    <TextActions
                        isGenerating={
                            isTextGenerating
                        }
                        onGenerate={
                            handleTextGenerate
                        }
                    />

                    <div>
                    </div>
                </section>
            </Card>
        </div>

        <aside className="post-creation-preview">
            <Card>
                <div className="preview-card">
                    <div className="preview-header">
                        <h2>Vorschau</h2>
                    </div>

                    <div className="preview-content">
                        <div className="preview-image-section">
                            <ImagePreview
                                isGenerating={
                                    isGenerating
                                }
                                generationStep={
                                    generationStep
                                }
                                transformedURL={
                                    transformedURL
                                }
                                templateURL={
                                    selectedCustomTemplate
                                        ? selectedCustomTemplate.backgroundImageUrl
                                        : currentTemplate.src
                                }
                            />
                        </div>

                        <div className="preview-text-section">
                            <PostTextPreview
                                postText={postText}
                                onChange={
                                    setPostText
                                }
                            />
                        </div>
                    </div>

                    <div className="post-preview-actions">
                        <button
                            type="button"
                            className="btn-primary"
                            onClick={
                                handleSavePost
                            }
                            disabled={
                                isSavingPost ||
                                (!postText &&
                                    !transformedURL)
                            }
                        >
                            {isSavingPost
                                ? "Speichert..."
                                : "Post speichern"}
                        </button>
                    </div>
                </div>
            </Card>
        </aside>
    </div>
);
}